<template>
	<div
		:class="{
			'with-errors': (withError || errors.length > 0) && showErrors,
			'with-success': displaySuccess && withSuccess && append !== 'tag',
			'with-suffix': hasSuffix,
			'with-prefix': hasPrefix,
			'on-focus': isFocus,
			'on-hover': isHover,
			'up-button-hover': upButtonHover,
			'down-button-hover': downButtonHover,
			'field--disabled': disabled,
			'field--empty': $refs.input && $refs.input.value.length === 0,
		}"
		class="field"
		data-cy="field"
	>
		<div class="field__heading">
			<label
				v-if="label"
				:for="`field-${_uid}`"
				class="field__label"
				data-cy="field-label"
			>
				{{ label }}
				<helio-information
					v-if="infoMessage"
					:message="infoMessage"
					:gap="infoMessageGap"
					:skin="infoMessageSkin"
					:color="infoColor"
					:icon="infoIcon"
					:icon-weight="infoIconWeight"
				/>
			</label>

			<p
				v-if="maxLength"
				:class="{
					'is-count-over': (characterCount < 0)
				}"
				class="field__indication"
			>
				{{ characterCount }}
			</p>
		</div>

		<div
			class="field__group"
			@mouseover="isHover = true"
			@mouseleave="isHover = false"
		>
			<div
				v-if="errorIcon || successIcon"
				class="field__validation"
			>
				<helio-icon
					v-if="errorIcon"
					:weight="errorIconWeight"
					:token="errorIcon"
					color="accent9-normal"
					custom-classes="validation__icon validation__icon--error"
				/>

				<helio-icon
					v-if="successIcon"
					:weight="errorIconWeight"
					:token="successIcon"
					color="accent6-normal"
					custom-classes="validation__icon validation__icon--success"
				/>
			</div>

			<div
				v-if="prepend && !multiline"
				:class="{
					'is-disabled' : disabled,
					'prepend--large': large,
				}"
				class="field__prepend"
				data-cy="field-prepend"
			>
				{{ prepend }}
			</div>

			<div
				v-if="icon && iconPosition === 'left'"
				class="field__icon field__icon--left"
				data-cy="field-icon"
			>
				<helio-icon
					:token="icon"
					:weight="iconWeight"
					:spin="iconSpin"
				/>
			</div>

			<input
				v-if="!multiline && mask"
				:id="`field-${_uid}`"
				ref="input"
				v-model="value"
				v-mask="mask"
				:type="type"
				:inputmode="inputMode"
				:placeholder="placeholder"
				:class="{
					'field__input--large' : (large),
					'field__input--iconLeft' : (icon && iconPosition === 'left'),
					'field__input--iconRight' : (icon && iconPosition === 'right')
				}"
				:disabled="disabled"
				:spellcheck="spellcheck"
				:autocomplete="autocomplete"
				:data-lpignore="autocomplete === 'off' ? true : false"
				:size="autowidth && value ? value.length : null"
				:maxlength="forceMaxLength && maxLength ? maxLength : null"
				:min="type === 'number' ? minNumber : null"
				:max="type === 'number' ? maxNumber : null"
				:step="type === 'number' ? 'any' : null"
				class="field__input"
				data-cy="field-input"
				@focus="focusField(value)"
				@blur="blurField(value)"
				@keyup.enter="$emit('enter', value)"
			>

			<input
				v-if="!multiline && !mask"
				:id="`field-${_uid}`"
				ref="input"
				v-model="value"
				:type="type"
				:inputmode="inputMode"
				:size="autowidth && value ? value.length : null"
				:placeholder="placeholder"
				:spellcheck="spellcheck"
				:class="{
					'field__input--large' : (large),
					'field__input--iconLeft' : (icon && iconPosition === 'left'),
					'field__input--iconRight' : (icon && iconPosition === 'right')
				}"
				:disabled="disabled"
				:autocomplete="autocomplete"
				:data-lpignore="autocomplete === 'off' ? true : false"
				:maxlength="forceMaxLength && maxLength ? maxLength : null"
				:min="type === 'number' ? minNumber : null"
				:max="type === 'number' ? maxNumber : null"
				:step="type === 'number' ? 'any' : null"
				class="field__input"
				data-cy="field-input"
				@focus="focusField(value)"
				@blur="blurField(value)"
				@keyup.enter="$emit('enter', value)"
			>

			<textarea
				v-if="multiline && type === 'text'"
				:id="`field-${_uid}`"
				ref="input"
				v-model="value"
				:placeholder="placeholder"
				:disabled="disabled"
				:rows="multiline"
				:spellcheck="spellcheck"
				:class="{
					'field__textarea--resizable': resizable,
					'field__textarea--large': large
				}"
				:maxlength="forceMaxLength && maxLength ? maxLength : null"
				class="field__textarea"
				data-cy="field-textarea"
				@focus="focusField(value)"
				@blur="blurField(value)"
				@keyup.enter="$emit('enter', value)"
			/>

			<div
				v-if="type === 'password'"
				class="field__password-toggle"
				data-cy="password-toggle"
				tabindex="-1"
				@click="showPassword(`field-${_uid}`, $event)"
			>
				<helio-icon
					token="eye"
					weight="light"
					custom-classes="icon--eye field__password-toggle--on"
					data-cy="toggle-on"
				/>

				<helio-icon
					token="eye-slash"
					weight="light"
					custom-classes="icon--eye field__password-toggle--off"
					data-cy="toggle-off"
				/>
			</div>

			<div
				v-if="icon && iconPosition === 'right'"
				class="field__icon field__icon--right"
			>
				<helio-icon
					:token="icon"
					:weight="iconWeight"
					:spin="iconSpin"
				/>
			</div>

			<div
				v-if="append && !multiline && append !== 'tag'"
				:class="{
					'is-disabled' : disabled,
					'append--large': large,
				}"
				class="field__append"
				data-cy="field-append"
				@click="$emit('appendClick', value)"
			>
				{{ append }}
			</div>

			<div
				v-if="append === 'tag'"
				class="field__append field__append--tag"
				@click="$emit('appendClick', value)"
			>
				<helio-icon
					weight="light"
					token="plus"
				/>
			</div>

			<div
				v-if="type === 'number'"
				class="field__updown-button"
				:class="{ 'is-disabled' : disabled }"
			>
				<div
					class="field__up-button"
					:class="{
						'field__up-button--hover': upButtonHover,
						'field__up-button--is-disabled' : disabled,
					}"
					data-cy="field-up-button"
					@mouseover="upButtonHover = true"
					@mouseleave="upButtonHover = false"
					@click="incrementNumber"
				>
					<helio-icon
						class="icon"
						:class="{ 'icon--is-disabled' : disabled }"
						token="angle-up"
					/>
				</div>

				<div
					class="field__down-button"
					:class="{
						'field__down-button--hover': downButtonHover,
						'field__down-button--is-disabled' : disabled,
					}"
					data-cy="field-down-button"
					@mouseover="downButtonHover = true"
					@mouseleave="downButtonHover = false"
					@click="decrementNumber"
				>
					<helio-icon
						class="icon"
						:class="{ 'icon--is-disabled' : disabled, }"
						token="angle-down"
					/>
				</div>
			</div>

			<helio-button
				v-if="(button || buttonIcon)"
				:class="{
					'field__button--icon-only': !button
				}"
				data-cy="field-button"
				:disabled="currentErrors && currentErrors.length > 0"
				:text="button"
				:icon="buttonIcon"
				:color="buttonColor"
				class="field__button"
				icon-position="right"
				@click="$emit('click', value)"
			/>
		</div>

		<div
			v-if="hasHint || hasErrors || footerSpacePresent"
			class="field__footing"
		>
			<div
				v-if="hasHint"
				class="field__indication field__indication--hint"
				data-cy="field-indication-hint"
			>
				{{ hint }}
			</div>

			<div
				v-if="hasErrors && hasErrorSlot && showErrors"
				role="alert"
				aria-relevant="all"
				aria-live="assertive"
				aria-atomic="false"
			>
				<slot name="error" />
			</div>

			<div
				v-else-if="hasErrors && showErrors"
				:tabindex="hasErrors ? '0' : '-1'"
				role="alert"
				aria-relevant="all"
				aria-live="assertive"
				aria-atomic="false"
			>
				<helio-errors
					v-if="errors.length > 0"
					:items="errors"
				/>

				<template v-if="showValidators">
					<div
						v-if="validator.minDate === false && minDate"
						class="field__indication"
						:aria-label="errorMessages.minDate || $t('field.min-date', [minDate])"
					>
						{{ errorMessages.minDate || $t('field.min-date', [minDate]) }}
					</div>

					<div
						v-if="validator.minLength === false && minLength"
						class="field__indication"
						:aria-label="errorMessages.minLength || $t('field.min-length', [minLength])"
					>
						{{ errorMessages.minLength || $t('field.min-length', [minLength]) }}
					</div>

					<div
						v-if="validator.maxLength === false && maxLength"
						class="field__indication"
						:aria-label="errorMessages.maxLength || $t('field.max-length', [maxLength])"
					>
						{{ errorMessages.maxLength || $t('field.max-length', [maxLength]) }}
					</div>

					<template v-if="type === 'number'">
						<div
							v-if="validator.minNumber === false
								&& !positiveNumber
								&& !hasMinAndMaxNumber
								&& minNumber !== null"
							class="field__indication"
							:aria-label="errorMessages.minNumber || $t('field.min-number', [minNumber])"
						>
							{{ errorMessages.minNumber || $t('field.min-number', [minNumber]) }}
						</div>

						<div
							v-if="validator.maxNumber === false
								&& !hasMinAndMaxNumber
								&& maxNumber !== null"
							class="field__indication"
							:aria-label="errorMessages.maxNumber || $t('field.max-number', [maxNumber])"
						>
							{{ errorMessages.maxNumber || $t('field.max-number', [maxNumber]) }}
						</div>

						<div
							v-if="validator.minMaxNumber === false
								&& hasMinAndMaxNumber"
							class="field__indication"
							:aria-label="errorMessages.minMaxNumber
								|| $t('field.min-max-number', [minNumber, maxNumber])"
						>
							{{ errorMessages.minMaxNumber || $t('field.min-max-number', [minNumber, maxNumber]) }}
						</div>

						<div
							v-if="validator.positiveNumber === false
								&& !hasMinAndMaxNumber"
							class="field__indication"
							:aria-label="errorMessages.positiveNumber || $t('field.positive-number')"
						>
							{{ errorMessages.positiveNumber || $t('field.positive-number') }}
						</div>

						<div
							v-if="validator.number === false"
							class="field__indication"
							:aria-label="errorMessages.number || $t('field.must-be-number')"
						>
							{{ errorMessages.number || $t('field.must-be-number') }}
						</div>
					</template>

					<div
						v-if="(type === 'url') && validator.url === false"
						class="field__indication"
						:aria-label="errorMessages.url || $t('field.must-be-valid-url')"
					>
						{{ errorMessages.url || $t('field.must-be-valid-url') }}
					</div>

					<div
						v-if="(type === 'text-only') && validator.textOnly === false"
						class="field__indication"
						:aria-label="errorMessages.textOnly || $t('field.must-be-text-only')"
					>
						{{ errorMessages.textOnly || $t('field.must-be-text-only') }}
					</div>

					<div
						v-if="(type === 'email') && !validator.email"
						class="field__indication"
						data-cy="must-be-email"
						:aria-label="errorMessages.email || $t('field.must-be-email')"
					>
						{{ errorMessages.email || $t('field.must-be-email') }}
					</div>

					<div
						v-if="(pattern !== null) && !validator.pattern"
						class="field__indication"
						:aria-label="errorMessages.pattern || $t('field.must-be-valid-format')"
					>
						{{
							`${errorMessages.pattern || $t('field.must-be-valid-format')}
							${hint ? ` &ndash; ${hint}`: ''}`
						}}
					</div>

					<div
						v-if="required && !validator.required"
						class="field__indication"
						:aria-label="errorMessages.required || $t('field.must-be-not-empty')"
					>
						{{ errorMessages.required || $t('field.must-be-not-empty') }}
					</div>
				</template>
			</div>
		</div>

		<helio-password-validator
			v-if="passwordValidation"
			:password-validation="passwordValidation"
			:password="value"
			@password-validation="$emit('password-validation', $event)"
		/>
	</div>
</template>

<script>
import stringValidator from 'validator';
import { mask } from 'vue-the-mask';
import Icon from '../icon/Icon.vue';
import Button from '../button/Button.vue';
import Errors from '../errors/Errors.vue';
import Information from '../information/Information.vue';
import PasswordValidator from './password-validator/PasswordValidator.vue';

export default {
	name: 'Field',

	components: {
		'helio-icon': Icon,
		'helio-button': Button,
		'helio-errors': Errors,
		'helio-information': Information,
		'helio-password-validator': PasswordValidator,
	},

	directives: {
		mask,
	},

	props: {
		append: {
			type: String,
			required: false,
			default: null,
			description: 'Sets content that will be added to the right-hand side of the input field',
		},

		autocomplete: {
			type: String,
			required: false,
			default: 'on',
			validator: value => [
				'off',
				'on',
			].indexOf(value) !== -1,
			description: 'Sets whether the Field will permit the browser to provide autocomplete options to the user',
		},

		autofocus: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the input is automatically focused on page load',
		},

		autowidth: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the width of Field will adjust in response to the number of characters entered by the user',
		},

		button: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether a button will be placed on the right-hand side of the Field component',
		},

		buttonColor: {
			type: String,
			required: false,
			default: 'primary',
			description: 'Sets the colour of the button',
		},

		buttonIcon: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the icon that will appear in the button',
		},

		disabled: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Field will be disabled for the user',
		},

		displaySuccess: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Applies a CSS class that will indicate a successful entry',
		},

		errorIcon: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether an icon will appear in the right-hand side of the field to indicate an incorrect entry',
		},

		errorIconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the errorIcon',
		},

		errorMessages: {
			type: Object,
			required: false,
			default: () => ({
				minLength: null,
				maxLength: null,
				minDate: null,
				minNumber: null,
				minMaxNumber: null,
				maxNumber: null,
				positiveNumber: null,
				number: null,
				url: null,
				textOnly: null,
				email: null,
				pattern: null,
				required: null,
			}),
			description: 'Sets custom error messages',
		},

		footerSpacePresent: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether a space will be added at the bottom of the Field component to accomodate a future hint',
		},

		forceMaxLength: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the maxLength prop limits the number of possible keystrokes in the Field',
		},

		hint: {
			type: String,
			required: false,
			default: null,
			description: 'Sets a hint that will appear beneath the input',
		},

		icon: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether an icon will appear in the Field and which icon it will be',
		},

		iconPosition: {
			type: String,
			required: false,
			default: 'left',
			validator: value => [
				'left',
				'right',
			].indexOf(value) !== -1,
			description: 'Sets the location of the icon',
		},

		iconSpin: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the icon will spin',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the icon',
		},

		infoColor: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the color of the infoIcon',
		},

		infoIcon: {
			type: String,
			required: false,
			default: 'question-circle',
			description: '',
		},

		infoIconWeight: {
			type: String,
			required: false,
			default: 'bold',
			description: 'Sets the weight of the icon that will appear next to the Field\'s label',
		},

		infoMessage: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the icon that will appear next to the Field\'s label. Clicking the icon will open a tooltip.',
		},

		infoMessageGap: {
			type: Number,
			required: false,
			default: 12,
			description: 'Sets the space in px between the anchor and the point of the Tooltip',
		},

		infoMessageSkin: {
			type: String,
			required: false,
			default: 'light',
			validator: value => [
				'dark',
				'light',
			].indexOf(value) !== -1,
			description: 'Sets whether the Tooltip will have a dark or light skin',
		},

		initialErrors: {
			type: Array,
			required: false,
			default: () => [],
			description: 'Sets the text that will appear in the hint',
		},

		initialValue: {
			type: [String, Number],
			required: false,
			default: null,
			description: 'Sets the default value of the Field',
		},

		inputMode: {
			type: String,
			required: false,
			default: null,
			validator: value => [
				'decimal',
				'email',
				'none',
				'numeric',
				'search',
				'tel',
				'text',
				'url',
			].indexOf(value) !== -1,
			description: 'Sets the virtual keyboard (inputmode attribute) for the field on mobile',
		},

		label: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text of the Field\'s label',
		},

		large: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets the height of the Field component',
		},

		mask: {
			type: String,
			required: false,
			default: null,
			description: 'Sets a mask that will only accept correctly formatted entries (e.g., "(###) ###-####")',
		},

		maxLength: {
			type: Number,
			required: false,
			default: null,
			validator: maxLength => maxLength > 0,
			description: 'Sets the maximum number of characters that may be entered in the Field',
		},

		maxNumber: {
			type: Number,
			required: false,
			default: null,
			description: 'Sets the maximum number value that may be entered in the Field',
		},

		minDate: {
			type: String,
			required: false,
			default: null,
			validator: minDate => minDate.match(/^\d{4}-\d{2}-\d{2}$/),
			description: 'Sets the earliest date accepted by the Field',
		},

		minLength: {
			type: Number,
			required: false,
			default: null,
			validator: minLength => minLength > 0,
			description: 'Sets the minimum number of characters that must be entered in the Field',
		},

		minNumber: {
			type: Number,
			required: false,
			default: null,
			description: 'Sets the minimum number value that may be entered in the Field',
		},

		multiline: {
			type: Number,
			required: false,
			default: null,
			validator: multiline => multiline > 0,
			description: 'Sets a default height of the Field measured in number of lines of text',
		},

		passwordValidation: {
			type: Object,
			required: false,
			default: null,
			description: 'Shows a visual indication of the password\'s validation',
		},

		pattern: {
			type: [String, RegExp],
			required: false,
			default: null,
			description: 'Sets a specific pattern an entry must match in order to be valid',
		},

		placeholder: {
			type: [String, Number],
			required: false,
			default: null,
			description: 'Sets the Field\'s placeholder text',
		},

		prepend: {
			type: String,
			required: false,
			default: null,
			description: 'Sets content that will be added to the left-hand side of the input field',
		},

		required: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether a user is obliged to provide an entry in the Field',
		},

		resizable: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the user can resize the field',
		},

		showErrors: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether errors will be shown',
		},

		showValidators: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the validator hint will be suppressed',
		},

		spellcheck: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether non-dictionary words will be underlined in red',
		},

		step: {
			type: Number,
			required: false,
			default: 1,
			description: 'Sets the step for increment and decrement buttons',
		},

		successIcon: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether an icon will appear in the right-hand side of the field to indicate a successful entry',
		},

		successIconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the success icon',
		},

		type: {
			type: String,
			required: false,
			default: 'text',
			validator: value => [
				'email',
				'number',
				'password',
				'text',
				'text-only',
				'url',
			].indexOf(value) !== -1,
			description: 'Sets the type of field (e.g., "number")',
		},
	},

	slots: {
		error: {
			required: false,
			scoped: false,
			description: 'Supplies a singular message regardless of the error',
		},
	},

	data() {
		return {
			value: '',
			errors: [],
			isFocus: false,
			hasBlurred: false,
			hasChanged: false,
			withSuccess: false,
			withError: false,
			isHover: false,
			upButtonHover: false,
			downButtonHover: false,
			characterCount: 0,
			validator: {
				text: null,
				number: null,
				textOnly: null,
				email: null,
				url: null,
				minDate: null,
				minLength: null,
				maxLength: null,
				minNumber: null,
				minMaxNumber: null,
				maxNumber: null,
				positiveNumber: null,
				required: null,
				pattern: null,
			},
		};
	},

	computed: {
		hasHint() {
			return this.hint && this.currentErrors.length === 0 && !this.disabled;
		},

		hasErrors() {
			// return false if the field has been completely emptied to its original state
			if (
				this.hasChanged
				&& this.value === ''
				&& this.validator.pattern === null
			) {
				return false;
			}

			return (
				this.errors.length > 0 // error is passed by parent
				|| (
					this.hasChanged
					&& this.currentErrors.length > 0 // errors from component's auto-validation
				)
			);
		},

		hasErrorSlot() {
			return !!this.$slots.error || !!this.$scopedSlots.error;
		},

		hasSuffix() {
			if (
				((this.icon && this.iconPosition === 'right')
				|| (this.append)
				|| (this.button)
				|| (this.buttonIcon))
				&& !this.multiline
			) {
				return true;
			}

			return false;
		},

		hasPrefix() {
			if (
				(this.icon && this.iconPosition === 'left')
				|| (this.prepend && !this.multiline)
			) {
				return true;
			}

			return false;
		},

		hasMinAndMaxNumber() {
			return this.minNumber && this.maxNumber;
		},

		positiveNumber() {
			return this.minNumber === 1;
		},

		currentErrors() {
			const list = [];

			Object.keys(this.validator).forEach((error) => {
				if (this.validator[error] === false) {
					list.push(error);
				}
			});

			return list;
		},
	},

	watch: {
		value(newValue) {
			this.checkValidity(newValue);
			this.hasChanged = true;
			this.characterCount = newValue ? (this.maxLength - this.value.length) : 0;
			this.$emit('changed', newValue);

			if (this.hasBlurred) {
				this.showValidation();
			}
		},

		autofocus() {
			if (this.autofocus) {
				this.autofocusField();
			}
		},

		initialValue(newValue) {
			this.value = newValue;
		},

		initialErrors(newErrors) {
			this.errors = newErrors;
		},

		validator: {
			// eslint-disable-next-line
			handler: function () {
				this.$emit('errors', this.currentErrors);
			},
			deep: true,
		},
	},

	mounted() {
		// Reset errors
		this.errors = [];

		if (this.initialErrors) {
			this.errors = this.initialErrors;
		}
	},

	created() {
		if (this.autofocus) {
			this.autofocusField();
		}

		if (this.initialValue !== null) {
			this.value = this.initialValue;
		}
	},

	methods: {
		autofocusField() {
			setTimeout(() => {
				if (this.$refs.input) {
					this.$refs.input.focus();
				}

				this.isFocus = true;
			}, 100);
		},

		initialValidator() {
			return {
				text: null,
				number: null,
				textOnly: null,
				email: null,
				url: null,
				maxLength: null,
				maxNumber: null,
				minDate: null,
				minLength: null,
				minNumber: null,
				minMaxNumber: null,
				positiveNumber: null,
				required: null,
				pattern: null,
			};
		},

		checkValidity(value) {
			if (value && value.length === 0 && !this.required) {
				this.validator = this.initialValidator();
				return false;
			}

			if (
				this.type === 'url'
				&& (!stringValidator.isURL(value, { require_protocol: true, require_host: false }))
			) {
				this.$set(this.validator, 'url', false);
			} else {
				this.$set(this.validator, 'url', true);
			}

			if (
				this.type === 'email'
				&& !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
			) {
				this.$set(this.validator, 'email', false);
			} else {
				this.$set(this.validator, 'email', true);
			}

			if (
				this.type === 'text-only'
				&& !/^[a-zÀ-ÿ]+$/.test(value)
			) {
				this.$set(this.validator, 'textOnly', false);
			} else {
				this.$set(this.validator, 'textOnly', true);
			}

			if (this.minDate) {
				const universalDate = this.$i18n.locale === 'fr'
					? this.frenchDateToUniversal(value)
					: this.englishDateToUniversal(value);

				this.$set(this.validator, 'minDate', universalDate >= this.minDate);
			} else {
				this.$set(this.validator, 'minDate', true);
			}

			if (
				this.minLength
				&& value
				&& value.length < this.minLength
			) {
				this.$set(this.validator, 'minLength', false);
			} else {
				this.$set(this.validator, 'minLength', true);
			}

			if (
				this.maxLength
				&& value
				&& value.length > this.maxLength
			) {
				this.$set(this.validator, 'maxLength', false);
			} else {
				this.$set(this.validator, 'maxLength', true);
			}

			if (
				this.type === 'number'
				&& this.hasMinAndMaxNumber
				&& value
				&& (this.maxNumber < value || this.minNumber > value)
			) {
				this.$set(this.validator, 'minMaxNumber', false);
			} else {
				this.$set(this.validator, 'minMaxNumber', true);
			}

			if (
				this.type === 'number'
				&& !this.hasMinAndMaxNumber
				&& this.maxNumber !== null
				&& value
				&& this.maxNumber < value
			) {
				this.$set(this.validator, 'maxNumber', false);
			} else {
				this.$set(this.validator, 'maxNumber', true);
			}

			if (
				this.type === 'number'
				&& !this.hasMinAndMaxNumber
				&& this.minNumber !== null
				&& value < 1
				&& this.minNumber === 1
			) {
				this.$set(this.validator, 'positiveNumber', false);
				this.$set(this.validator, 'minNumber', true);
			} else if (
				this.type === 'number'
				&& !this.hasMinAndMaxNumber
				&& this.minNumber !== null
				&& value
				&& this.minNumber > value
			) {
				this.$set(this.validator, 'minNumber', false);
				this.$set(this.validator, 'positiveNumber', true);
			} else {
				this.$set(this.validator, 'minNumber', true);
				this.$set(this.validator, 'positiveNumber', true);
			}

			// If all other conditions are valid, check the actual number validity
			if (
				this.type === 'number'
				&& this.validator.minMaxNumber === true
				&& this.validator.maxNumber === true
				&& this.validator.minNumber === true
				&& this.validator.positiveNumber === true
				&& !this.$refs.input.checkValidity()
			) {
				this.$set(this.validator, 'number', false);
			} else {
				this.$set(this.validator, 'number', true);
			}

			if (
				this.pattern !== null
				&& (
					this.initialValue !== value
					|| (this.initialValue === value && value !== '')
				)
				&& !RegExp(this.pattern).test(value)
			) {
				this.$set(this.validator, 'pattern', false);
			} else {
				this.$set(this.validator, 'pattern', true);
			}

			if (
				this.required
				&& value.length === 0
			) {
				this.$set(this.validator, 'required', false);
			} else {
				this.$set(this.validator, 'required', true);
			}

			return true;
		},

		showValidation() {
			if (this.value.length > 0) {
				if (this.currentErrors.length === 0 && this.hasChanged) {
					this.withSuccess = true;
					this.withError = false;
				} else if (this.currentErrors.length > 0 && this.hasChanged) {
					this.withSuccess = false;
					this.withError = true;
				}
			} else if (this.currentErrors.length === 0) {
				this.withSuccess = false;
				this.withError = false;
			} else {
				this.withSuccess = false;
				this.withError = true;
			}
		},

		showPassword(id, $event) {
			const el = $event.currentTarget;
			const field = this.$refs.input;

			el.classList.toggle('is-visible');

			if (field.type === 'password') {
				field.type = 'text';
			} else {
				field.type = 'password';
			}
		},

		focusField(value) {
			this.isFocus = true;
			this.$emit('focused', value);
		},

		blurField(value) {
			this.isFocus = false;
			this.hasBlurred = true;
			this.$emit('blurred', value);
			this.showValidation();
		},

		incrementNumber() {
			if (!!this.maxNumber && (this.maxNumber <= this.value)) {
				return;
			}
			this.value = Number(this.value) + this.step;
			this.$emit('arrow-clicked', this.value);
		},

		decrementNumber() {
			if (!!this.minNumber && (this.minNumber >= this.value)) {
				return;
			}
			this.value = Number(this.value) - this.step;
			this.$emit('arrow-clicked', this.value);
		},
	},
};
</script>

<style lang="scss" src="./field.scss" />
